import React, { useEffect, useState } from "react";
import { Save, RotateCcw, Loader2, CheckCircle, FileText, LayoutTemplate, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import AutoTextarea from "./AutoTextarea";

/**
 * VisitDefaultsEditor — Impostazioni → "Referti base".
 * Fonte unica: i Modelli cartella (Impostazioni → Modelli cartella).
 * Selezionato un modello, mostra SOLO i blocchi di quel modello (stesso ordine)
 * e permette di impostare il testo default di referto per ciascun blocco,
 * più i campi speciali (anamnesi, diagnosi sana, terapia base, protocollo, referto libero).
 */

const GROUP_LABELS = {
  anamnesi: "Anamnesi",
  visus: "Visus",
  refrazione: "Refrazione",
  tono: "Tono",
  segmento_anteriore: "Segmento anteriore",
  segmento_posteriore: "Segmento posteriore",
  diagnostica: "Diagnostica",
  motilita: "Motilità oculare / Ortottica",
  chiusura: "Chiusura",
};

export default function VisitDefaultsEditor() {
  const [services, setServices] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [values, setValues] = useState({});
  const [isCustomized, setIsCustomized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/medico/visit-defaults/${encodeURIComponent(id)}`);
      setBlocks(data.blocks || []);
      setSpecials(data.special_fields || []);
      const v = {};
      (data.blocks || []).forEach((b) => { v[b.key] = b.value || ""; });
      (data.special_fields || []).forEach((f) => { v[f.key] = f.value || ""; });
      setValues(v);
      setIsCustomized(!!data.is_customized);
    } catch (e) {
      toast.error(apiErr(e, "Errore caricamento referto base"));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/medico/visit-defaults");
        const list = data.services || [];
        setServices(list);
        const first = list[0]?.id;
        if (first) {
          setActiveId(first);
          await loadDetail(first);
        }
      } catch (e) {
        toast.error(apiErr(e, "Errore caricamento modelli"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectService = async (id) => {
    setActiveId(id);
    await loadDetail(id);
  };

  const onChange = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(values).forEach(([k, v]) => { if (typeof v === "string" && v.trim() !== "") payload[k] = v; });
      const { data } = await api.put(`/medico/visit-defaults/${encodeURIComponent(activeId)}`, payload);
      setBlocks(data.blocks || []);
      setSpecials(data.special_fields || []);
      setIsCustomized(true);
      setServices((p) => p.map((s) => (s.id === activeId ? { ...s, is_customized: true } : s)));
      toast.success("Referto base salvato");
    } catch (e) {
      toast.error(apiErr(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const applySystemDefaults = () => {
    setValues((p) => {
      const next = { ...p };
      [...blocks, ...specials].forEach((b) => {
        if (!(next[b.key] || "").trim() && b.system_default) next[b.key] = b.system_default;
      });
      return next;
    });
    toast.success("Default sano applicato ai campi vuoti — ricordati di salvare");
  };

  const resetSystem = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 6000);
      return;
    }
    setConfirmReset(false);
    setResetting(true);
    try {
      await api.delete(`/medico/visit-defaults/${encodeURIComponent(activeId)}`);
      await loadDetail(activeId);
      setServices((p) => p.map((s) => (s.id === activeId ? { ...s, is_customized: false } : s)));
      toast.success("Ripristinato ai default di sistema");
    } catch (e) {
      toast.error(apiErr(e, "Errore ripristino"));
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-slate-500">
        <Loader2 className="animate-spin mx-auto mb-2" /> Caricamento referti base…
      </div>
    );
  }

  // Raggruppa i blocchi per gruppo mantenendo l'ordine del modello
  const grouped = [];
  blocks.forEach((b) => {
    const last = grouped[grouped.length - 1];
    if (last && last.group === b.group) last.items.push(b);
    else grouped.push({ group: b.group, items: [b] });
  });

  const renderField = (b) => (
    <div key={b.key}>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
        <FileText size={10} /> {b.label}
      </label>
      <AutoTextarea
        value={values[b.key] || ""}
        onChange={(val) => onChange(b.key, val)}
        minRows={2}
        placeholder={b.system_default || "—"}
        data-testid={`visit-defaults-field-${b.key}`}
        className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm leading-relaxed"
      />
      <div className="text-[10px] text-slate-400 mt-0.5 italic">
        Default sistema: <span className="text-slate-500">{(b.system_default || "(vuoto)").slice(0, 130)}{b.system_default?.length > 130 ? "…" : ""}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="visit-defaults-editor">
      <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 text-xs text-brand-navy">
        <b>Referti base per modello cartella.</b> Seleziona un modello (configurato in <b>Modelli cartella</b>): vedrai
        solo i blocchi di quel modello, nello stesso ordine. I testi impostati qui vengono usati da <b>Tutto sano</b> e
        dalla dettatura "tutto nella norma" in cartella. Fonte unica: nessun doppione con altri default.
      </div>

      {/* Selettore modello cartella */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Modello cartella / Ambulatorio</label>
        <select
          data-testid="visit-defaults-service-select"
          value={activeId}
          onChange={(e) => selectService(e.target.value)}
          className="w-full sm:w-96 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-bold text-brand-navy"
        >
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label} ({s.blocks_count} blocchi){s.is_customized ? "  ●" : ""}
            </option>
          ))}
        </select>
        <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
          {isCustomized ? <><CheckCircle size={11} className="text-emerald-600" /> Referto base personalizzato per questo modello.</> : <>Default di sistema (nessuna personalizzazione attiva).</>}
        </div>
      </div>

      {detailLoading ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-slate-500">
          <Loader2 className="animate-spin mx-auto mb-2" /> Caricamento blocchi del modello…
        </div>
      ) : (
        <>
          {/* Blocchi del modello, nell'ordine del modello */}
          {grouped.length === 0 && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 text-center text-sm text-slate-500" data-testid="visit-defaults-no-blocks">
              Questo modello non ha blocchi configurati. Vai in <b>Impostazioni → Modelli cartella</b> per aggiungerli.
            </div>
          )}
          {grouped.map((g, gi) => (
            <div key={`${g.group}-${gi}`} className="bg-white rounded-xl border border-[#E2E8F0] p-5 space-y-3" data-testid={`visit-defaults-group-${g.group}`}>
              <div className="flex items-center gap-2 text-xs font-extrabold text-brand-navy uppercase tracking-wider">
                <LayoutTemplate size={12} className="text-brand-azure" /> {GROUP_LABELS[g.group] || g.group}
              </div>
              {g.items.map(renderField)}
            </div>
          ))}

          {/* Campi speciali di referto */}
          <div className="bg-white rounded-xl border-2 border-brand-azure/30 p-5 space-y-3" data-testid="visit-defaults-specials">
            <div className="text-xs font-extrabold text-brand-navy uppercase tracking-wider">Campi referto (diagnosi, terapia, protocollo)</div>
            {specials.map(renderField)}
          </div>
        </>
      )}

      {/* Pulsanti */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || detailLoading}
          data-testid="visit-defaults-save"
          className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salva referto base
        </button>
        <button
          type="button"
          onClick={applySystemDefaults}
          disabled={detailLoading}
          data-testid="visit-defaults-apply-healthy"
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60"
          title="Compila i campi vuoti con i valori 'sano' preimpostati Prime Vision (poi salva)"
        >
          <Sparkles size={14} />
          Applica default sano
        </button>
        <button
          type="button"
          onClick={resetSystem}
          disabled={resetting || !isCustomized}
          data-testid="visit-defaults-reset"
          className={`${confirmReset ? "bg-red-600 hover:bg-red-700 text-white border-red-600" : "bg-white hover:bg-slate-50 text-slate-700 border-[#E2E8F0]"} border font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50`}
          title={!isCustomized ? "Nessuna personalizzazione attiva da ripristinare" : "Cancella personalizzazione e ripristina i default Prime Vision (solo questo modello)"}
        >
          {resetting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          {confirmReset ? "Confermi il ripristino?" : "Ripristina default Prime Vision"}
        </button>
      </div>
    </div>
  );
}
